// client/src/utils/urlUtils.js
// URL utilities for routing in Storykin

/**
 * Convert course name to URL-safe slug
 * @param {string} courseName - The course_name field from Firestore
 * @returns {string} URL-safe slug
 */
export const courseNameToSlug = (courseName) => {
  if (!courseName) return '';
  
  return courseName
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '') // Remove special characters except spaces and hyphens
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Replace multiple consecutive hyphens with single hyphen
    .replace(/^-|-$/g, ''); // Remove leading/trailing hyphens
};

/**
 * Convert URL slug back to course name for database lookup
 * @param {string} slug - URL slug
 * @returns {string} Original course name format
 */
export const slugToCourseName = (slug) => {
  if (!slug) return '';
  
  return slug
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

/**
 * Find course by slug from a list of courses
 * @param {Array} courses - Array of course objects
 * @param {string} slug - URL slug to match
 * @returns {Object|null} Matching course object or null
 */
export const findCourseBySlug = (courses, slug) => {
  if (!courses || !slug) return null;
  
  return courses.find(course => 
    courseNameToSlug(course.course_name) === slug
  ) || null;
};

/**
 * Generate course overview URL
 * @param {Object} course - Course object with course_name
 * @returns {string} Course overview URL
 */
export const getCourseOverviewUrl = (course) => {
  if (!course?.course_name) return '/courses';
  
  return `/courses/${courseNameToSlug(course.course_name)}`;
};

/**
 * Generate lesson URL
 * @param {Object} course - Course object with course_name
 * @param {number} levelNumber - Level number (1-6)
 * @param {number} lessonNumber - Lesson number within the level (optional)
 * @returns {string} Lesson URL
 */
export const getLessonUrl = (course, levelNumber, lessonNumber = null) => {
  if (!course?.course_name || !levelNumber) return '/courses';
  
  const baseUrl = `/courses/${courseNameToSlug(course.course_name)}/level/${levelNumber}`;
  
  if (lessonNumber) {
    return `${baseUrl}/lesson/${lessonNumber}`;
  }
  
  return baseUrl;
};

/**
 * Get lesson number within a level based on lesson's actual lesson_number property
 * @param {Object} lesson - Lesson object with lesson_number property
 * @param {Array} levelLessons - All lessons in the same level (for validation)
 * @returns {number} Lesson number from the lesson object
 */
export const getLessonNumber = (lesson, levelLessons) => {
  if (!lesson) return 1;
  
  // Return the actual lesson_number property from the lesson object
  return lesson.lesson_number || 1;
};

/**
 * Convert prompt title to URL-safe slug
 * @param {string} promptTitle - The title field from Firestore
 * @returns {string} URL-safe slug
 */
export const promptTitleToSlug = (promptTitle) => {
  if (!promptTitle) return '';
  
  return promptTitle
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, '') // Remove special characters except spaces and hyphens
    .replace(/\s+/g, '-') // Replace spaces with hyphens
    .replace(/-+/g, '-') // Replace multiple consecutive hyphens with single hyphen
    .replace(/^-|-$/g, ''); // Remove leading/trailing hyphens
};

/**
 * Find prompt by slug from a list of prompts
 * @param {Array} prompts - Array of prompt objects
 * @param {string} slug - URL slug to match
 * @returns {Object|null} Matching prompt object or null
 */
export const findPromptBySlug = (prompts, slug) => {
  if (!prompts || !slug) return null;
  
  return prompts.find(prompt => 
    promptTitleToSlug(prompt.title) === slug
  ) || null;
};

/**
 * Generate prompt URL
 * @param {Object} prompt - Prompt object with title
 * @returns {string} Prompt URL
 */
export const getPromptUrl = (prompt) => {
  if (!prompt?.title) return '/prompts';
  
  return `/prompts/${promptTitleToSlug(prompt.title)}`;
};

/**
 * Admin URL utilities
 */

/**
 * Generate admin section URL
 * @param {string} section - Admin section name
 * @returns {string} Admin section URL
 */
export const getAdminUrl = (section = 'account') => {
  return `/admin/${section}`;
};

/**
 * Generate admin student statistics detail URL
 * @param {string} userId - Student user ID
 * @param {string} organizationId - Organization context for stats
 * @returns {string} Admin student stats URL
 */
export const getAdminStudentStatsUrl = (userId, organizationId) => {
  const params = new URLSearchParams({ org: organizationId });
  return `/admin/students/${userId}?${params.toString()}`;
};

/**
 * Generate admin edit URLs
 * @param {string} type - Type of resource (course, lesson, prompt)
 * @param {string} id - Resource ID
 * @returns {string} Admin edit URL
 */
export const getAdminEditUrl = (type, id) => {
  if (!type) return '/admin';
  
  if (type === 'organization') {
    return `/admin/organizations/${id || 'new'}`;
  }
  
  if (!id || id === 'new') {
    return `/admin/${type}s/new`;
  }
  
  return `/admin/${type}s/${id}/edit`;
};

/**
 * Parse admin URL parameters
 * @param {string} pathname - Current pathname
 * @param {string} search - Current search params
 * @returns {object} Parsed admin parameters
 */
export const parseAdminUrl = (pathname, search) => {
  const pathParts = pathname.split('/');
  const searchParams = new URLSearchParams(search);
  
  const section = pathParts[2] || 'account';
  const resourceId = pathParts[3];
  const action = pathParts[4]; // 'edit' for edit pages
  const orgId = searchParams.get('org');
  
  return {
    section,
    resourceId,
    action,
    orgId,
    isEdit: action === 'edit'
  };
};
